import { css } from '@emotion/react';
import styled from '@emotion/styled';

import materialSymbol from 'storybookConfig/mixins/materialSymbol';
import type { ThemeColor } from '../theme';

export const HEIGHT = '100px';

type StepStatusProp = {
  /* The status of the step */
  status?: 'default' | 'active' | 'completed';
  /* The color of the step in progress */
  activeColor?: ThemeColor;
};

type CircleProps = StepStatusProp;
type StepTextProps = StepStatusProp;
type WrapperProps = StepStatusProp;

interface StepProps extends StepStatusProp {
  /* The text to display below the circled number */
  children: React.ReactNode;
}

const CIRCLE_SIZE = 24;

const Circle = styled.span<CircleProps>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  border-radius: 100%;

  font-size: ${CIRCLE_SIZE / 2}px;
  margin-bottom: 16px;

  &::before,
  &::after {
    position: absolute;
    content: '';
    display: block;
    height: 4px;
    border-radius: 4px;
    width: calc(50% - ${CIRCLE_SIZE / 2 + 4}px);
    background-color: ${({ theme }) => theme.color.green500};
    z-index: 1;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`;

const StepLineContainer = styled.div`
  width: 100%;
  height: ${CIRCLE_SIZE}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepLine = styled.div<CircleProps>`
  height: 2px;
  border-radius: 4px;
  opacity: 0.7;
  background-color: ${({ theme }) => theme.color.white};
  display: flex;
  align-items: center;
  width: 100%;
  ${({ theme, status, activeColor }) =>
    status === 'active' &&
    css`
      height: 4px;
      background-color: ${activeColor || theme.color.white};
      opacity: 1;
    `}
`;

const CompletedCircle = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${CIRCLE_SIZE}px;
  height: ${CIRCLE_SIZE}px;
  border-radius: 100%;

  ${({ theme }) => css`
    color: ${theme.color.blue500};
    background: ${theme.color.green500};
  `};

  ${materialSymbol({ name: 'check' })}
`;

const StepText = styled.span<StepTextProps>`
  text-align: left;
  width: 100%;
  white-space: nowrap;
`;

const Wrapper = styled.div<WrapperProps>`
  flex: 1;+  
  min-width: 100px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 12px;

  ${({ status }) =>
    status === 'active' &&
    css`
      flex: 2;
    `}
`;

/**
 * A step UI. Intended to be used within the `Stepper` component.
 */
const Step = ({ status = 'default', activeColor, children }: StepProps) => {
  return (
    <Wrapper status={status}>
      {status === 'completed' ? (
        <Circle>
          <CompletedCircle />
        </Circle>
      ) : (
        <StepLineContainer>
          <StepLine status={status} activeColor={activeColor} />
        </StepLineContainer>
      )}
      {status === 'active' && <StepText status={status}>{children}</StepText>}
    </Wrapper>
  );
};

/**
 * A `Card` wrapper for `Step` components, displaying them properly in a row.
 *
 * Exports `Step` for convenience.
 */
const Stepper = styled.div`
  display: flex;
  gap: 24px;
  height: ${HEIGHT};
  width: 100%;
  padding: 12px 0;
`;

export { Step };
export default Stepper;
